import { CssBaseline } from '@material-ui/core'
import CustomTheme from './components/portico/theme'
import { ThemeProvider } from '@material-ui/core/styles'
import React, { Suspense, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import { Outlet } from 'react-router'
import AdfScreenerPersonalInfoPage from './adf/adf-screener/adf-screener-personal-info-page'
import AdfScreenerInUsaPage from './adf/adf-screener/adf-screener-in-usa-page'
import './App.css'
import { AuthenticatedRoute } from './components/authenticated-route'
import { ConnectPage } from './components/connect/connect-page'
import ContactInfo from './components/contact-info/contact-info'
import { EntityRoutesSwitch } from './components/entity-routes-switch'
import { Error } from './components/error'
import { ErrorMsgGeneral } from './components/error-msg-general'
import { InUsa } from './components/in-usa'
import { Portal } from './components/portal'
import SessionMgmt from './components/session-mgmt/session-mgmt'
import { SyncAccountingThankYou } from './components/sync-accounting-thank-you'
import { SyncYourAccounting } from './components/sync-your-accounting/sync-your-accounting'
import { ThankYou } from './components/thank-you'
import { Verify } from './components/verify'
import { VerifyEmail } from './components/verify-email'
import WelcomePage from './components/welcome/welcome-page/welcome-page'
import { getCurrentEnv } from './config/env'
import { featureToggles as initialToggles } from './config/feature-toggles'
import { FeatureToggleContext, featureToggleContextValue } from './config/toggle.context'
import { SnackBar } from './shared/snack-bar'
import { fetchUserInfo } from './stores/async/async-actions'
import { setUserAuthenticated } from './stores/user/user-action'
import { useUserAuthenticated } from './stores/user/user.hooks'
import './styles.css'
import AdfsContextProvider from './svber/adf-tracking/adf-context-provider'
import * as Svber from './svber/components-combined-import'
import CONSTANTS from './utils/constants'
import { isLoginExpired, refreshUserSession } from './utils/identity-utils'
import { UserList } from './svber/components'
import { DisconnectionManagement } from './svber/components/disconnection-management'
import { SvberAuthenticator } from './svber-authenticator'

const App: React.FC = () => {
    let [featureToggles, setFeatureToggles] = useState(initialToggles)
    const currentEnv = getCurrentEnv(window.location.hostname)
    const featureToggleContext = featureToggleContextValue(featureToggles, setFeatureToggles, currentEnv)
    const { useToggle } = featureToggleContext
    const toggleConnectPage = useToggle('ftConnectPage')
    const connectPage = toggleConnectPage(false, true)
    const params = window.location.search
    const dispatch = useDispatch()
    const userAuthenticated = useUserAuthenticated()

    useEffect(() => {
        new URLSearchParams(params).forEach((value, key) => {
            featureToggleContext.updateToggle(key, value.split(','))
        })
    }, [params])

    // Fetch userInfo from BFF if user is authenticated
    useEffect(() => {
        const isTokenExpired = isLoginExpired()

        if (userAuthenticated && !isTokenExpired) {
            // User is authenticated and the token is not yet expired, so we grab the user Info
            dispatch(fetchUserInfo())
        } else if (!userAuthenticated && !isTokenExpired) {
            // Coming back to the app from a refresh
            dispatch(setUserAuthenticated(true))
            refreshUserSession()
        }
    }, [userAuthenticated, dispatch])

    function AuthenticatedLayout() {
        return (
            <>
                <SessionMgmt
                    userAuthenticated
                    idleTimeoutDuration={CONSTANTS.IDLE_TIMEOUT_DURATION}
                    refreshBufferDuration={CONSTANTS.REFRESH_BUFFER_DURATION_SECONDS}
                    eventThrottleDuration={CONSTANTS.EVENT_THROTTLE_DURATION}
                    promptBeforeIdleDuration={CONSTANTS.PROMPT_BEFORE_IDLE_DURATION}
                />
                <Outlet />
            </>
        )
    }

    return (
        <ThemeProvider theme={CustomTheme}>
            <FeatureToggleContext.Provider value={featureToggleContext}>
                <CssBaseline />
                <Router>
                    <div className="App">
                        {/************** NOTE !!!!!! *************/}
                        {/*Order of the routes is important. When creating a new Route or modifying these, be sure to check*/}
                        {/*all other main route entry points (eg. /, /portal, DLA journey pages and ADF connect, /svber and /svber/sso */}
                        <Routes>
                            {/* Shown after a new DLA applicant submits on /in-usa */}
                            <Route path="/verifyemail" element={<VerifyEmail />} />

                            {/********* Public - General **********/}
                            <Route path="/error-msg-general" element={<ErrorMsgGeneral />} />
                            <Route path="/business-type/error" element={<Error />} />
                            <Route path="/thank-you" element={<ThankYou />} />

                            {/********* Public - Login/Auth **********/}
                            <Route path="/" element={<Navigate to="/welcome" />}>
                                {' '}
                            </Route>
                            <Route path="/welcome" element={<WelcomePage />} />
                            <Route path="/login" element={<WelcomePage />} />
                            <Route path="/verify" element={<Verify />} />

                            {/********* Public - DLA **********/}
                            <Route path="/in-usa" element={<InUsa />} />
                            <Route path="/in-usa/error" element={<Error />} />
                            <Route path="/contact-info" element={<ContactInfo />} />

                            {/********* Public - ADF **********/}
                            {connectPage && <Route path="/connect" element={<ConnectPage />} />}
                            {connectPage && <Route path="/connect/personal-info" element={<AdfScreenerPersonalInfoPage />} />}
                            {connectPage && <Route path="/connect/in-usa" element={<AdfScreenerInUsaPage />} />}
                            {connectPage && <Route path="/connect/thank-you" element={<SyncAccountingThankYou />} />}

                            {/********* SVBER App **********/}
                            {/********* Public - SVBers **********/}
                            <Route
                                path="/svber/error"
                                element={
                                    <Suspense fallback={<div>Loading...</div>}>
                                        <Svber.SvberErrorPage />
                                    </Suspense>
                                }
                            >
                                {' '}
                            </Route>

                            <Route path="/svber" element={<Navigate to="/svber/login" />}></Route>
                            <Route
                                path="/svber/login"
                                element={
                                    <Suspense fallback={<div>Loading...</div>}>
                                        <Svber.SvberLoginPage />
                                    </Suspense>
                                }
                            ></Route>
                            <Route
                                path="/svber/error"
                                element={
                                    <Suspense fallback={<div>Loading...</div>}>
                                        <Svber.SvberErrorPage />
                                    </Suspense>
                                }
                            >
                                {' '}
                            </Route>
                            <Route path="/svber" element={<Navigate to="/svber/login" />}></Route>
                            <Route
                                path="/svber/login"
                                element={
                                    <Suspense fallback={<div>Loading...</div>}>
                                        <Svber.SvberLoginPage />
                                    </Suspense>
                                }
                            ></Route>

                            {/********* Restricted - SVBers **********/}

                            <Route path="/svber/sso" element={<Navigate to="/svber/data-management/companies/view" />}></Route>

                            {/*** Successful Okta login re-direction path config.  ***/}
                            <Route path="/svber/data-management" element={<Navigate to="/svber/data-management/companies/view" />}>
                                {' '}
                            </Route>

                            <Route
                                path="/svber/data-management/companies/:layout"
                                element={
                                    <Suspense fallback={<div>Loading...</div>}>
                                        <SvberAuthenticator>
                                            <Svber.DataManagementView />
                                        </SvberAuthenticator>
                                    </Suspense>
                                }
                            ></Route>

                            <Route
                                path="/svber/applications"
                                element={
                                    <Suspense fallback={<div>Loading...</div>}>
                                        <SvberAuthenticator>
                                            <Svber.Applications />
                                        </SvberAuthenticator>
                                    </Suspense>
                                }
                            ></Route>
                            <Route
                                path="/svber/data-management/company-profile/:id"
                                element={
                                    <Suspense fallback={<div>Loading...</div>}>
                                        <SvberAuthenticator>
                                            <Svber.CompanyProfileView />
                                        </SvberAuthenticator>
                                    </Suspense>
                                }
                            ></Route>

                            <Route
                                path="/svber/admin-panel"
                                element={
                                    <Suspense fallback={<div>Loading...</div>}>
                                        <SvberAuthenticator>
                                            <Svber.AdminPanelView />
                                        </SvberAuthenticator>
                                    </Suspense>
                                }
                            >
                                <Route path="users" element={<UserList />} />
                                <Route path="disconnection-management" element={<DisconnectionManagement />} />

                                <Route path="" element={<Navigate to={`users`} />}></Route>
                            </Route>
                            <Route
                                path="/svber/adf-tracking"
                                element={
                                    <Suspense fallback={<div>Loading...</div>}>
                                        <SvberAuthenticator>
                                            <AdfsContextProvider>
                                                <Svber.ADFTracking />
                                            </AdfsContextProvider>
                                        </SvberAuthenticator>
                                    </Suspense>
                                }
                            ></Route>

                            {/********* Restricted - DLA and ADF actions and Portal **********/}

                            {/* Note: AuthenticatedRoute checks a user's cookies to see if the session is still valid. */}
                            {/* If not valid, it redirects to Welcome page. If valid, it redirects to one of the below components. */}
                            <Route
                                element={
                                    <AuthenticatedRoute>
                                        <AuthenticatedLayout />
                                    </AuthenticatedRoute>
                                }
                            >
                                <Route path="/portal" element={<Portal />}></Route>
                                {connectPage && <Route path="/connect/sync-your-accounting" element={<SyncYourAccounting />} />}
                                <Route path="/*" element={<EntityRoutesSwitch />}></Route>
                            </Route>
                        </Routes>
                        <SnackBar />
                    </div>
                </Router>
            </FeatureToggleContext.Provider>
        </ThemeProvider>
    )
}

export default App

// Helpers

export function configMedallia(): void {
    try {
        if (window['KAMPYLE_ONSITE_SDK'] && typeof window['KAMPYLE_ONSITE_SDK'].updatePageView === 'function') {
            window['KAMPYLE_ONSITE_SDK'].updatePageView()
        }
        window['moduleTitle'] = window.location.pathname
    } catch (error) {
        console.log(error)
    }
}
